import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Plus, Search, Pencil, Trash2, ShoppingCart, ArrowDownToLine, History } from "lucide-react";
import { ConsumableForm, emptyConsumableForm, consumableToForm, buildConsumablePayload } from "../printers/ConsumableForm";
import { MovementDialog } from "../printers/MovementDialog";
import { MovementsList } from "../printers/MovementsList";
import { CONSUMABLE_TYPES, CONSUMABLE_COLORS, StockBadge, labelOf, fmtDate, fmtMoney } from "../printers/constants";

const COLOR_DOT = { black: "bg-gray-900", cyan: "bg-cyan-400", magenta: "bg-pink-500", yellow: "bg-yellow-400", color: "bg-gradient-to-r from-cyan-400 via-pink-500 to-yellow-400" };

export default function PrinterConsumablesView({ sites, companies, printers, onChanged, refreshKey, searchParams }) {
    const navigate = useNavigate();
    const { hasPermission } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [printerFilter, setPrinterFilter] = useState(searchParams.get("printer") || "");
    const [lowOnly, setLowOnly] = useState(searchParams.get("low") === "1");
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(emptyConsumableForm());
    const [movement, setMovement] = useState(null);
    const [history, setHistory] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (typeFilter) params.type = typeFilter;
            if (printerFilter) params.printer_id = printerFilter;
            if (lowOnly) params.low_stock = 1;
            const r = await api.getConsumables(params);
            setItems(r.consumables);
        } catch { toast.error("Erreur de chargement"); }
        finally { setLoading(false); }
    }, [search, typeFilter, printerFilter, lowOnly]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const refresh = () => { load(); onChanged(); };
    const defaultSite = sites.find((s) => s.is_default)?.id || "";

    const save = async () => {
        if (!form.name.trim()) { toast.error("Nom requis"); return; }
        setSaving(true);
        try {
            if (dialog.mode === "edit") { await api.updateConsumable(dialog.item.id, buildConsumablePayload(form)); toast.success("Consommable mis à jour"); }
            else { const c = await api.createConsumable(buildConsumablePayload(form)); toast.success(`"${c.name}" ajouté`); }
            setDialog(null);
            refresh();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Supprimer le consommable "${c.name}" ?`)) return;
        try { await api.deleteConsumable(c.id); toast.success("Consommable supprimé"); refresh(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const openHistory = async (c) => {
        setHistory({ item: c, movements: null });
        try { setHistory({ item: c, movements: await api.getConsumableMovements(c.id) }); }
        catch { setHistory({ item: c, movements: [] }); }
    };

    const deleteMovement = async (m) => {
        if (!window.confirm("Annuler ce mouvement ? Le stock sera recalculé.")) return;
        try { await api.deletePrinterMovement(m.id); toast.success("Mouvement annulé"); openHistory(history.item); refresh(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div data-testid="consumables-view">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, référence..." className="pl-9" data-testid="consumable-search" />
                </div>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="consumable-type-filter">
                    <option value="">Tous types</option>
                    {CONSUMABLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <select value={printerFilter} onChange={(e) => setPrinterFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white max-w-[220px]" data-testid="consumable-printer-filter">
                    <option value="">Toutes imprimantes</option>
                    {printers.map((p) => <option key={p.id} value={p.id}>{p.name}{p.site_name ? ` · ${p.site_name}` : ""}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} data-testid="consumable-low-filter" />Stock bas uniquement
                </label>
                {hasPermission("printers:manage") && <Button onClick={() => { setForm(emptyConsumableForm(defaultSite)); setDialog({ mode: "create" }); }} className="ml-auto" data-testid="create-consumable-btn"><Plus className="w-4 h-4 mr-1" />Nouveau consommable</Button>}
            </div>

            <div className="card">
                <div className="card-body p-0 overflow-x-auto">
                    {loading ? <p className="text-sm text-gray-400 py-8 text-center animate-pulse">Chargement...</p>
                        : items.length === 0 ? <p className="text-sm text-gray-400 py-8 text-center">Aucun consommable</p> : (
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-gray-100 text-left">
                                    {["Consommable", "Type", "Compatible avec", "Stock", "Prix unit.", "Fournisseur", "Dernier achat", ""].map((h) => (
                                        <th key={h} className={`px-4 py-2.5 text-xs font-medium text-gray-500 ${h === "" ? "text-right" : ""}`}>{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {items.map((c) => (
                                        <tr key={c.id} className={`border-b border-gray-50 hover:bg-gray-50 ${c.quantity <= 0 ? "bg-red-50/40" : c.is_low ? "bg-orange-50/40" : ""}`} data-testid={`consumable-row-${c.id}`}>
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    {c.color && COLOR_DOT[c.color] && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[c.color]}`} title={labelOf(CONSUMABLE_COLORS, c.color)} />}
                                                    <div>
                                                        <div className="font-medium text-gray-900">{c.name}</div>
                                                        <div className="text-[11px] text-gray-500 font-mono">{c.reference || ""}{c.storage_location ? <span className="font-sans"> · {c.storage_location}</span> : ""}{c.site_name ? <span className="font-sans text-teal-600"> · {c.site_name}</span> : ""}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-gray-600">{labelOf(CONSUMABLE_TYPES, c.type)}</td>
                                            <td className="px-4 py-2.5 text-xs">
                                                {c.compatible_printers.length === 0 ? <span className="text-gray-300">—</span> : c.compatible_printers.slice(0, 3).map((p) => (
                                                    <button key={p.id} onClick={() => navigate(`/admin/inventory/printers/${p.id}`)} className="block text-teal-700 hover:underline text-left truncate max-w-[180px]">{p.name}</button>
                                                ))}
                                                {c.compatible_printers.length > 3 && <span className="text-[10px] text-gray-400">+{c.compatible_printers.length - 3} autre(s)</span>}
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-base font-bold font-mono ${c.quantity <= 0 ? "text-red-600" : c.is_low ? "text-orange-600" : "text-gray-800"}`} data-testid={`consumable-qty-${c.id}`}>{c.quantity}</span>
                                                    <div>
                                                        <StockBadge consumable={c} testId={`consumable-stock-${c.id}`} />
                                                        <div className="text-[10px] text-gray-400">seuil {c.min_quantity}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-gray-600">{fmtMoney(c.unit_price, c.currency)}</td>
                                            <td className="px-4 py-2.5 text-xs text-gray-600">{c.company_name || "—"}</td>
                                            <td className="px-4 py-2.5 text-xs text-gray-600">{fmtDate(c.last_purchase_date)}</td>
                                            <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                                <Button variant="ghost" size="sm" onClick={() => setMovement({ item: c, kind: "purchase" })} title="Enregistrer un achat" data-testid={`consumable-purchase-${c.id}`}><ShoppingCart className="w-3.5 h-3.5 text-emerald-600" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => setMovement({ item: c, kind: "consumption" })} title="Consommer / installer" disabled={c.quantity <= 0} data-testid={`consumable-consume-${c.id}`}><ArrowDownToLine className="w-3.5 h-3.5 text-blue-600" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => openHistory(c)} title="Historique des mouvements" data-testid={`consumable-history-${c.id}`}><History className="w-3.5 h-3.5" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => { setForm(consumableToForm(c)); setDialog({ mode: "edit", item: c }); }} title="Modifier" data-testid={`consumable-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                                {hasPermission("admin") && <Button variant="ghost" size="sm" onClick={() => remove(c)} title="Supprimer" data-testid={`consumable-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                </div>
            </div>

            <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{dialog?.mode === "edit" ? `Modifier — ${dialog.item.name}` : "Nouveau consommable"}</DialogTitle></DialogHeader>
                    <div className="py-2 max-h-[65vh] overflow-y-auto pr-1">
                        <ConsumableForm form={form} setForm={setForm} printers={printers} companies={companies} sites={sites} isEdit={dialog?.mode === "edit"} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(null)}>Annuler</Button>
                        <Button onClick={save} disabled={saving} data-testid="consumable-save-btn">{saving ? "..." : dialog?.mode === "edit" ? "Enregistrer" : "Créer"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <MovementDialog consumable={movement?.item || null} kind={movement?.kind || "purchase"} printers={printers} companies={companies} onClose={() => setMovement(null)} onDone={refresh} />

            <Dialog open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-4 h-4 text-teal-600" />Historique — {history?.item.name}</DialogTitle></DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto" data-testid="consumable-history-dialog">
                        {history && !history.movements ? <p className="text-xs text-gray-400 py-6 text-center">Chargement...</p>
                            : <MovementsList movements={history?.movements || []} showConsumable={false} onDelete={hasPermission("admin") ? deleteMovement : undefined} />}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setHistory(null)}>Fermer</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
